import React from 'react'

import cx from 'classnames'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

import StoreLogo from '@/assets/images/store_logo.png'
import { useLocale } from '@/features/locale/useLocale'
import { UserAvatarImage } from '@/features/user/avatar/UserAvatarImage'
import { LOGIN_STATUS } from '@/features/user/userSlice'
import { GAME_STATUS } from '@/fixtures/games'
import { shortSlugToGameNameKey } from '@/localization/utils'
import { useAppSelector } from '@/utils/useAppSelector'

import { useFetchProfileQueryState } from '../../api'
import { Loader } from '../../components/Loader'
import { COLOR_GRAY } from '../../utils/styles'
import { useGames } from '../common/useGames'
import { useIsLoggedInV2 } from '../common/useIsLoggedIn'
import { CartButton } from './CartButton'
import { DesktopNavMenu } from './DesktopNavMenu'
import { LoginButton } from './LoginButton'
import { MobileNav } from './MobileNav'

import styles from './Header.module.scss'

export interface NavLink {
  href: string
  label: string
}

const HeaderUser: React.FC<{ isLoggedIn: boolean; showFullUsername?: boolean }> = ({
  showFullUsername = false,
  isLoggedIn,
}) => {
  const { isHydrated } = useIsLoggedInV2()
  const locale = useLocale()
  const { data, isLoading } = useFetchProfileQueryState(locale)
  const username = data?.profile?.profile?.name

  if (isLoading || !isHydrated) {
    return (
      <div className={styles.User}>
        <Loader strokeColor={COLOR_GRAY} dataCy="header-user-loading" />
      </div>
    )
  }
  if (!isLoggedIn) {
    return (
      <div className={cx(styles.User, styles.LoggedOut)}>
        <LoginButton additionalClasses={styles.LoginButton} data-cy="header-login" />
      </div>
    )
  }

  return (
    <div className={styles.User}>
      <Link href="/account">
        <div data-cy="header-user-avatar">
          {showFullUsername && <p>{username}</p>}
          <UserAvatarImage
            showOutline
            showInitial={!showFullUsername}
            className={styles.UserAvatar}
            layout="fill"
            quality={100}
            objectFit="contain"
          />
        </div>
      </Link>
    </div>
  )
}

const CheckoutHeader: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
  const t = useTranslations()
  return (
    <header className={styles.Header}>
      <Link href="/">
        <div className={styles.Logo}>
          <div className={styles.LogoContainer}>
            <Image src={StoreLogo} alt={t('sitename')} layout="fill" objectFit="contain" quality={100} />
          </div>
        </div>
      </Link>
      <HeaderUser showFullUsername isLoggedIn={isLoggedIn} />
    </header>
  )
}

export const LandingHeader: React.FC<{ isLoggedIn: boolean }> = ({ isLoggedIn }) => {
  const t = useTranslations()

  const games = useGames()
  const gameLinks = games
    .filter((game) => game.status === GAME_STATUS.ENABLED)
    .map(({ slug, shortSlug }) => ({ href: `/game/${slug}`, label: t(shortSlugToGameNameKey(shortSlug)) }))

  const infoLinks = [
    { label: t('about_title'), href: '/about' },
    { label: t('help_title'), href: '/help' },
  ]
  const links = [...gameLinks, ...infoLinks]

  return (
    <header className={styles.Header}>
      <Link href="/" passHref>
        <a tabIndex={0}>
          <div className={styles.Logo}>
            <div className={styles.LogoContainer}>
              <Image src={StoreLogo} alt={t('sitename')} layout="fill" objectFit="contain" quality={100} />
            </div>
          </div>
        </a>
      </Link>
      <DesktopNavMenu primary={links} isLoggedIn={isLoggedIn} />
      <div className={styles.Actions}>
        <CartButton className={styles.Cart} />
        <HeaderUser isLoggedIn={isLoggedIn} />
        <div className={styles.MobileNavMenu}>
          <MobileNav gameLinks={gameLinks} infoLinks={infoLinks} />
        </div>
      </div>
    </header>
  )
}

export const Header: React.FC = () => {
  const { pathname } = useRouter()
  const isCheckout = pathname.startsWith('/checkout')
  const isLoggedIn = useAppSelector((state) => state.user.loginStatus === LOGIN_STATUS.LOGGED_IN)

  return (
    <div className={cx(styles.HeaderWrapper, { [styles.Checkout]: isCheckout })}>
      {isCheckout ? <CheckoutHeader isLoggedIn={isLoggedIn} /> : <LandingHeader isLoggedIn={isLoggedIn} />}
    </div>
  )
}
